import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * KINHOUSE delegated AJAX add-to-cart + free-gift threshold manager.
 *
 * Add-to-cart: covers the cart-drawer "Complete your ritual" upsell forms
 * (.khc-up) and the offer-page buy box (form id obxForm...). Both are plain
 * /cart/add posts as a no-JS fallback; this module intercepts them, adds via
 * the AJAX API with the drawer section requested in the same call, and
 * resolves a CartLinesUpdateEvent so cart-items-component morphs in place
 * and cart-icon updates. The buy box additionally opens the drawer.
 *
 * Free gift: keeps the $0 gift product in sync with the cart total. At or
 * above the threshold the gift is added automatically; below it, removed.
 * If the shopper removes the gift themselves while still eligible, it stays
 * out for the rest of the session. Quantity is clamped to 1.
 *
 * This lives in an asset loaded from cart-drawer.liquid (outside the morphed
 * region) because inline scripts inside the drawer are skipped when the
 * drawer content is morphed in from the empty-cart state.
 */

const GIFT_VARIANT_ID = 71162115522813;
const GIFT_THRESHOLD = 7500; // cents, matches the drawer meter
const GIFT_SOURCE = 'kh-gift';

const guard = '__khAjaxCart';
if (!window[guard]) {
  window[guard] = true;

  /* "Choose a different scent" in the empty drawer. Cross-page clicks
     navigate on their own; close the drawer first so it isn't left open in
     bfcache. Same-page clicks are hash-only, so the browser does nothing:
     close the drawer ourselves and scroll to the buybox. */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('.khe__alt');
    if (!link) return;
    const drawer = document.getElementById('cart-drawer');
    const url = new URL(link.getAttribute('href'), window.location.origin);
    if (url.pathname === window.location.pathname) {
      event.preventDefault();
      if (drawer && typeof drawer.close === 'function') drawer.close();
      const target = url.hash && document.querySelector(url.hash);
      setTimeout(() => {
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (url.hash) history.replaceState(null, '', url.pathname + url.hash);
      }, 60);
    } else if (drawer && typeof drawer.close === 'function') {
      drawer.close();
    }
  });

  /* A drawer that server-rendered EMPTY ships no cart-row component scripts.
     When rows are morphed in after an add, their <script type="module"> tags
     are injected but never executed, so cart-items-component (remove buttons,
     quantity steppers) never registers. Dynamic import() runs them properly;
     the module cache makes repeat calls free. */
  /* Register the cart-row components on every page load. A page whose cart
     is empty at render time ships no component-cart-* script tags at all, so
     the first rows morphed into the drawer would arrive with no remove or
     quantity handlers. The URLs are derived from this module's own asset URL,
     so the theme version prefix is always right. */
  ['component-cart-items.js', 'component-cart-quantity-selector.js'].forEach((name) => {
    import(new URL(name, import.meta.url).href).catch(() => {});
  });

  const hydrateDrawerScripts = () => {
    document.querySelectorAll('#cart-drawer script[type="module"][src], script[type="module"][src*="component-cart"]').forEach((tag) => {
      import(tag.src).catch(() => {});
    });
  };

  const cartSectionIds = () => {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el.dataset.sectionId) ids.push(el.dataset.sectionId);
    });
    if (!ids.length && document.getElementById('cart-drawer')) ids.push('cart-drawer-section');
    return ids;
  };

  const fetchCart = () =>
    fetch(Theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((r) => r.json());

  /** Add or change a line via the AJAX API and broadcast the update. */
  const mutateCart = (url, payload, action, merchandiseId) => {
    const ids = cartSectionIds();
    const body = { ...payload };
    if (ids.length) {
      body.sections = ids.join(',');
      body.sections_url = window.location.pathname;
    }
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'cart',
        lines: [{ merchandiseId: String(merchandiseId), quantity: payload.quantity }],
        promise: deferred.promise,
      })
    );
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then((r) => r.json())
      .then((response) => {
        if (response.status && response.message) throw new Error(response.message);
        return fetchCart().then((cart) => {
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              sections: response.sections,
              items: cart.items,
              itemCount: cart.item_count,
              source: GIFT_SOURCE,
              didError: false,
            },
          });
          return cart;
        });
      })
      .catch((error) => {
        deferred.reject(error);
        throw error;
      });
  };

  // ---- free-gift threshold sync ----
  let giftBusy = false;
  let giftPending = false;

  const syncGift = async () => {
    /* A sync requested while one was already running used to be dropped on the
       floor. That is how the gift went missing: two cart changes in quick
       succession, the second sync discarded, and nothing put the gift in until
       the next page load. Remember the request and run it on the way out. */
    if (giftBusy) {
      giftPending = true;
      return;
    }
    giftBusy = true;
    try {
      const cart = await fetchCart();
      const giftLine = cart.items.find((i) => i.variant_id === GIFT_VARIANT_ID);
      const totalExGift = cart.total_price - (giftLine ? giftLine.final_line_price : 0);
      const eligible = totalExGift >= GIFT_THRESHOLD;

      if (eligible && !giftLine) {
        // Dismissal is recorded where it actually happens (see the remove
        // listener below). Inferring it from "eligible but no gift line" could
        // not tell a deliberate removal apart from a sync that never ran, so a
        // single dropped sync used to suppress the gift for the whole session.
        if (!sessionStorage.getItem('khGiftDismissed')) {
          await mutateCart(Theme.routes.cart_add_url, { id: GIFT_VARIANT_ID, quantity: 1 }, 'add', GIFT_VARIANT_ID);
        }
      } else if (!eligible && giftLine) {
        await mutateCart('/cart/change.js', { id: giftLine.key, quantity: 0 }, 'remove', GIFT_VARIANT_ID);
      } else if (giftLine && giftLine.quantity > 1) {
        await mutateCart('/cart/change.js', { id: giftLine.key, quantity: 1 }, 'update', GIFT_VARIANT_ID);
      }

      /* Dropping below the threshold ends the offer, so it also ends the
         refusal. Without this the flag outlived the cart that caused it: remove
         the gift once and it stayed suppressed for the whole tab, even after
         emptying the cart and starting over -- while the Liquid meter, which
         only reads the cart total, still announced it as unlocked. */
      if (!eligible) {
        sessionStorage.removeItem('khGiftDismissed');
      }
    } catch (e) {
      /* leave the cart as-is; the next update retries */
    } finally {
      giftBusy = false;
      if (giftPending) {
        giftPending = false;
        queueGiftSync();
      }
    }
  };

  /* Record a dismissal only when the shopper removes the gift themselves.
     Line item keys are "<variant id>:<hash>", so the gift's own row is
     identifiable without a further cart fetch. Capture phase, because the row
     is detached while the click is still being handled. */
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const button = target.closest('.cart-items__remove');
      if (!button) return;
      const row = button.closest('[data-key]');
      if (row && String(row.dataset.key).split(':')[0] === String(GIFT_VARIANT_ID)) {
        sessionStorage.setItem('khGiftDismissed', '1');
      }
    },
    true
  );

  let giftTimer = null;
  const queueGiftSync = () => {
    /* Every cart mutation funnels through here, whatever triggered it --
       upsell, buy box, or a PDP form handled by Horizon's own product-form.
       Any of them can be the first thing to put rows into a drawer that
       server-rendered empty, so this is the one reliable place to make sure
       the row components' scripts actually run. */
    hydrateDrawerScripts();
    clearTimeout(giftTimer);
    giftTimer = setTimeout(syncGift, 350);
  };

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    /* Not every dispatcher attaches a promise -- the PDP buy box fires a plain
       CustomEvent. Optional chaining used to swallow those silently, so a cart
       add from the PDP never triggered a gift sync and the gift only turned up
       on the next page load. */
    if (!event.promise || typeof event.promise.then !== 'function') {
      queueGiftSync();
      return;
    }
    event.promise
      .then(({ detail }) => {
        if (detail?.source === GIFT_SOURCE) return;
        queueGiftSync();
      })
      .catch(() => {});
  });

  /* Let a section settle the gift itself before it paints the drawer. */
  window.khSyncGift = syncGift;

  queueGiftSync();

  // ---- delegated AJAX add-to-cart ----
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const isUpsell = form.classList.contains('khc-up');
    // form.id is shadowed by the <input name="id"> control; read the attribute.
    const isBuybox = String(form.getAttribute('id') || '').indexOf('obxForm') === 0;
    if (!isUpsell && !isBuybox) return;

    event.preventDefault();

    const button = form.querySelector(isUpsell ? '.khc-up__add' : '.obx__atc');
    const buttonText = button ? button.textContent : '';
    const wasDisabled = button && button.disabled;
    if (button) {
      button.disabled = true;
      if (isUpsell) button.textContent = 'Adding…';
    }
    const restore = () => {
      if (button && !wasDisabled) {
        button.disabled = false;
        if (isUpsell) button.textContent = buttonText;
      }
    };

    const formData = new FormData(form);
    /* The empty-state re-offer lives inside an empty drawer, and Horizon's
       cart-items-component does not repaint from empty on an external add.
       Remember the state so the drawer can be re-rendered after the gift settles. */
    const drawerWasEmpty = isUpsell && !!document.querySelector('#cart-drawer .cart-drawer--empty');
    formData.delete('return_to');

    /* The offer buy box can hold a different scent per bottle, which a single
       id + quantity cannot express. When its slots report more than one, post
       items[] instead and collapse duplicate scents into one line. */
    let jsonBody = null;
    const slots = form.querySelector('[id^="khss-"]');
    if (slots) {
      const uid = slots.id.slice(5);
      const getIds = window['khssIds_' + uid];
      const ids = typeof getIds === 'function' ? getIds().filter(Boolean) : [];
      if (ids.length > 1) {
        const counts = {};
        ids.forEach((id) => { counts[id] = (counts[id] || 0) + 1; });
        jsonBody = JSON.stringify({
          items: Object.keys(counts).map((id) => ({ id: Number(id), quantity: counts[id] })),
        });
      }
    }

    const ids = cartSectionIds();
    if (ids.length) {
      formData.append('sections', ids.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [
          {
            merchandiseId: String(formData.get('id')),
            quantity: Number(formData.get('quantity')) || 1,
          },
        ],
        promise: deferred.promise,
      })
    );

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      body: jsonBody || formData,
      headers: jsonBody
        ? { Accept: 'application/json', 'Content-Type': 'application/json' }
        : { Accept: 'application/json' },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) throw new Error(response.message || 'Add to cart failed');
        return fetchCart().then((cart) => {
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              sections: response.sections,
              items: cart.items,
              itemCount: cart.item_count,
              source: 'kh-ajax-cart',
              didError: false,
            },
          });
          /* Settle the gift before the drawer is shown. This used to open the
             drawer first and queue the sync afterwards, so a cart that had just
             crossed the threshold painted without its gift and only filled in
             seconds later -- which read as the gift not showing up. */
          return syncGift().then(() => {
            /* Horizon's own cart-items-component repaints the drawer from the
               event above. Re-render ONLY if that didn't happen (the drawer
               still shows its empty state) -- rendering unconditionally races
               the morph and can leave the new rows' controls half-wired. */
            const stillEmpty = drawerWasEmpty && document.querySelector('#cart-drawer .cart-drawer--empty');
            if (stillEmpty) {
              return sectionRenderer.renderSection('cart-drawer-section', {
                cache: false,
                mode: 'hydration',
                injectStylesheet: true,
              });
            }
          }).then(() => {
            if (drawerWasEmpty) hydrateDrawerScripts();
          }).then(() => {
            if (isBuybox) {
              const drawer = document.getElementById('cart-drawer');
              if (drawer && typeof drawer.open === 'function') drawer.open();
            }
            restore();
          });
        });
      })
      .catch((error) => {
        deferred.reject(error);
        restore();
      });
  });
}
